import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  Container,
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';

import config from '../../js/config';
import { getJWTRole, getJWTSub, getBearerString } from '../../js/jwt_token';

const TabPanel = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
};

const ProfileViewer = () => {
  const [tabValue, setTabValue] = useState(0);
  const [apiKeys, setApiKeys] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [generatedKeys, setGeneratedKeys] = useState(null);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (tabValue === 1) fetchApiKeys();
  }, [tabValue]);

  const handleTabChange = (event, newValue) => {
    if (newValue === 2) {
      localStorage.removeItem('isaacToken');
      navigate('/login');
      return;
    }
    setTabValue(newValue);
  };

  const fetchApiKeys = async () => {
    try {
      const response = await fetch(`${config.data_platform_api_url}/api-keys/`, {
        headers: {
          Authorization: getBearerString(),
        },
      });
      if (response.ok) {
        const data = await response.json();
        setApiKeys(data.api_keys);
      } else {
        console.error('Failed to fetch API keys');
      }
    } catch (error) {
      console.error('Error fetching API keys:', error);
    }
  };

  const generateRandomKey = () => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    return Array.from({ length: 32 }, () => characters[Math.floor(Math.random() * characters.length)]).join('');
  };

  const handleGenerateApiKey = async () => {
    try {
      const accessKey = generateRandomKey();
      const secretKey = generateRandomKey();

      const payload = {
        name: keyName,
        expiration_date: expirationDate || null,
        access_key: accessKey,
        secret_key: secretKey,
      };

      const response = await fetch(`${config.data_platform_api_url}/api-keys/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getBearerString(),
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setGeneratedKeys({ accessKey, secretKey });
        setWarningDialogOpen(true);
        fetchApiKeys();
        setDialogOpen(false);
        setKeyName('');
        setExpirationDate('');
      } else {
        console.error('Failed to create API key');
      }
    } catch (error) {
      console.error('Error creating API key:', error);
    }
  };

  const handleDeleteApiKey = async (accessKey) => {
    try {
      const response = await fetch(`${config.data_platform_api_url}/api-keys/${accessKey}`, {
        method: 'DELETE',
        headers: {
          Authorization: getBearerString(),
        },
      });
      if (response.ok) {
        fetchApiKeys();
      } else {
        console.error('Failed to delete API key');
      }
    } catch (error) {
      console.error('Error deleting API key:', error);
    }
  };

  return (
    <Container maxWidth="md">
      <Typography variant="h4" gutterBottom>
        Profile Viewer
      </Typography>

      <Paper elevation={3}>
        <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
          <Tab label="Profile" />
          <Tab label="API Keys" />
          <Tab label="Logout" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6">User Information</Typography>
          <Box mt={2}>
            <Typography><strong>Name:</strong> {getJWTSub()} </Typography>
            <Typography><strong>Role:</strong> {getJWTRole().join(',')} </Typography>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6">API Keys</Typography>
          <Box mt={2}>
            <Button variant="contained" color="primary" onClick={() => setDialogOpen(true)}>
              Generate API Key
            </Button>
            <Box mt={2}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>ID</strong></TableCell>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell><strong>Access Key</strong></TableCell>
                      <TableCell><strong>Created At</strong></TableCell>
                      <TableCell><strong>Expiration Date</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {apiKeys.map((key) => (
                      <TableRow key={key.id}>
                        <TableCell>{key.id}</TableCell>
                        <TableCell>{key.name}</TableCell>
                        <TableCell>{key.access_key}</TableCell>
                        <TableCell>{key.created_date}</TableCell>
                        <TableCell>{key.expiration_date || 'None'}</TableCell>
                        <TableCell>
                          <Button color="secondary" onClick={() => handleDeleteApiKey(key.access_key)}>
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </TabPanel>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Generate API Key</DialogTitle>
        <DialogContent>
          <TextField
            label="Key Name"
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Expiration Date (optional)"
            type="date"
            value={expirationDate}
            onChange={(e) => setExpirationDate(e.target.value)}
            fullWidth
            margin="dense"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleGenerateApiKey} color="primary">
            Generate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={warningDialogOpen} onClose={() => setWarningDialogOpen(false)}>
        <DialogTitle>API Key Generated</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Your API key has been generated. Please save the following information as the secret key will not be accessible again.
          </Typography>
          <Box mt={2}>
            <Typography><strong>Access Key:</strong> {generatedKeys?.accessKey}</Typography>
            <Typography><strong>Secret Key:</strong> {generatedKeys?.secretKey}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarningDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProfileViewer;
